import { useState, type FormEvent } from 'react';

import { StateMessage } from '@/components/ui/controls';
import { Badge, Card, Field } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import { errorOf, useAssistant } from '@/features/assistant/use-assistant';
import http from '@/utils/http';

import SettingsHeader from './settings-nav';

type Step = 'start' | 'code' | 'password';

/**
 * Assistant's Telegram account (ASSIST-1): an admin signs it in with the account's number,
 * the code Telegram sends and, when the account has one, its two-step password. Prisma keeps
 * only the session; the password is not kept.
 */
const AssistantSettingsPage = () => {
  const status = useAssistant();
  const [step, setStep] = useState<Step>('start');
  const [appId, setAppId] = useState('');
  const [appHash, setAppHash] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const send = async (path: string, body: Record<string, string>) => {
    setBusy(true);
    setProblem(null);
    try {
      const res = await http.post<{ step: string }>(path, body);
      if (res.data.step === 'done') {
        setStep('start');
        setCode('');
        setPassword('');
        status.reload();
      } else {
        setStep(res.data.step === 'password' ? 'password' : 'code');
      }
    } catch (err) {
      setProblem(errorOf(err));
      if (path !== '/assistant/sign-in') setStep('start');
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    setBusy(true);
    try {
      await http.post('/assistant/sign-out');
      status.reload();
    } catch (err) {
      setProblem(errorOf(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (step === 'start') void send('/assistant/sign-in', { app_id: appId, app_hash: appHash, phone });
    else if (step === 'code') void send('/assistant/code', { code });
    else void send('/assistant/password', { password });
  };

  const st = status.data;
  return (
    <div>
      <SettingsHeader />
      {st && !st.enabled ? (
        <StateMessage title="Assistant is off here">It is turned on per environment (prisma-assistant).</StateMessage>
      ) : (
        <div className="max-w-2xl space-y-4">
          <Card title="Assistant's Telegram account" aside={st && <Badge tone={st.connected ? 'ok' : 'neutral'}>{st.connected ? 'Connected' : 'Not connected'}</Badge>}>
            <dl>
              <Field label="Account">{st?.name ? `${st.name}${st.username ? ` (@${st.username})` : ''}` : '—'}</Field>
              <Field label="Number">{st?.phone || '—'}</Field>
              <Field label="Answers">{st?.answers ? 'Written by the language model' : 'Off: no language model here'}</Field>
            </dl>
            {st?.error && !st.connected && <p className="mt-3 text-sm text-danger">It stopped: {st.error}</p>}
            {st?.phone && (
              <button type="button" className={`${btn.secondary} mt-4`} onClick={signOut} disabled={busy}>
                Sign out
              </button>
            )}
          </Card>

          <Card title={st?.connected ? 'Connect another account' : 'Connect the account'}>
            <form onSubmit={submit} className="space-y-4">
              {step === 'start' && (
                <>
                  <p className="text-sm text-fg-muted">
                    Get the api_id and the api_hash at my.telegram.org, under API development tools, signed in with the account’s number. Telegram then sends a code to the account’s apps.
                  </p>
                  <div>
                    <label htmlFor="asst-app-id" className={field.label}>
                      api_id
                    </label>
                    <input id="asst-app-id" className={field.input} value={appId} onChange={(e) => setAppId(e.target.value)} inputMode="numeric" autoComplete="off" required />
                  </div>
                  <div>
                    <label htmlFor="asst-app-hash" className={field.label}>
                      api_hash
                    </label>
                    <input id="asst-app-hash" className={field.input} value={appHash} onChange={(e) => setAppHash(e.target.value)} autoComplete="off" spellCheck={false} required />
                  </div>
                  <div>
                    <label htmlFor="asst-phone" className={field.label}>
                      The account’s phone number, with the country code
                    </label>
                    <input id="asst-phone" className={field.input} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" placeholder="+40 7…" required />
                  </div>
                </>
              )}
              {step === 'code' && (
                <div>
                  <label htmlFor="asst-code" className={field.label}>
                    The code Telegram sent to the account’s apps
                  </label>
                  <input id="asst-code" className={field.input} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" required autoFocus />
                </div>
              )}
              {step === 'password' && (
                <div>
                  <label htmlFor="asst-password" className={field.label}>
                    The account’s two-step verification password (not kept)
                  </label>
                  <input id="asst-password" type="password" className={field.input} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required autoFocus />
                </div>
              )}
              {problem && <p className="text-sm text-danger">{problem}</p>}
              <button type="submit" className={btn.primary} disabled={busy}>
                {busy ? 'Waiting for Telegram…' : step === 'start' ? 'Send the code' : 'Sign in'}
              </button>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AssistantSettingsPage;
